import React from 'react';

export type IconName =
  | 'home'
  | 'shield'
  | 'settings'
  | 'backup'
  | 'users'
  | 'log'
  | 'map'
  | 'search'
  | 'download'
  | 'upload'
  | 'trash'
  | 'play'
  | 'stop'
  | 'restart'
  | 'plus'
  | 'x'
  | 'chevron'
  | 'discord'
  | 'globe'
  | 'block'
  | 'warn'
  | 'clock'
  | 'cloud'
  | 'sun'
  | 'moon'
  | 'logout'
  | 'chat'
  | 'skull'
  | 'check'
  | 'refresh'
  | 'external'
  | 'pause'
  | 'activity'
  | 'menu';

interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

/** Minimal line icon set (original, from the v2 design). */
export const Icon: React.FC<IconProps> = ({ name, size = 16, style, ...rest }) => {
  const s = { width: size, height: size, flex: 'none', ...style };
  const c = {
    strokeWidth: 1.8,
    stroke: 'currentColor',
    fill: 'none',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  const base = { viewBox: '0 0 24 24', style: s, 'aria-hidden': true, ...c, ...rest };
  switch (name) {
    case 'home':
      return <svg {...base}><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /></svg>;
    case 'shield':
      return <svg {...base}><path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" /></svg>;
    case 'settings':
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19 12a7 7 0 00-.1-1.3l2-1.5-2-3.4-2.3.9a7 7 0 00-2.3-1.3L13.8 3h-3.6l-.5 2.4a7 7 0 00-2.3 1.3l-2.3-.9-2 3.4 2 1.5A7 7 0 005 12c0 .4 0 .9.1 1.3l-2 1.5 2 3.4 2.3-.9a7 7 0 002.3 1.3l.5 2.4h3.6l.5-2.4a7 7 0 002.3-1.3l2.3.9 2-3.4-2-1.5c0-.4.1-.9.1-1.3z" />
        </svg>
      );
    case 'backup':
      return <svg {...base}><path d="M21 12a9 9 0 11-3-6.7" /><path d="M21 4v5h-5" /></svg>;
    case 'users':
      return (
        <svg {...base}>
          <circle cx="9" cy="8" r="3.5" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
          <circle cx="17" cy="9" r="3" /><path d="M15 14c3.3.3 6 2 6 5" />
        </svg>
      );
    case 'log':
      return <svg {...base}><path d="M4 6h16M4 12h16M4 18h10" /></svg>;
    case 'map':
      return <svg {...base}><path d="M3 7v14l6-3 6 3 6-3V4l-6 3-6-3-6 3z" /><path d="M9 4v14M15 7v14" /></svg>;
    case 'search':
      return <svg {...base}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>;
    case 'download':
      return <svg {...base}><path d="M12 4v12" /><path d="m7 11 5 5 5-5" /><path d="M5 20h14" /></svg>;
    case 'upload':
      return <svg {...base}><path d="M12 20V8" /><path d="m7 13 5-5 5 5" /><path d="M5 4h14" /></svg>;
    case 'trash':
      return <svg {...base}><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>;
    case 'play':
      return <svg {...base} fill="currentColor"><path d="M6 4l14 8L6 20V4z" /></svg>;
    case 'stop':
      return <svg {...base} fill="currentColor"><rect x="6" y="6" width="12" height="12" /></svg>;
    case 'restart':
      return (
        <svg {...base}>
          <path d="M4 12a8 8 0 0114-5.3" /><path d="M20 4v5h-5" />
          <path d="M20 12a8 8 0 01-14 5.3" /><path d="M4 20v-5h5" />
        </svg>
      );
    case 'refresh':
      return <svg {...base}><path d="M21 12a9 9 0 11-3-6.7" /><path d="M21 4v5h-5" /></svg>;
    case 'plus':
      return <svg {...base}><path d="M12 5v14M5 12h14" /></svg>;
    case 'x':
      return <svg {...base}><path d="M6 6l12 12M18 6 6 18" /></svg>;
    case 'check':
      return <svg {...base}><path d="m5 12 5 5 9-10" /></svg>;
    case 'chevron':
      return <svg {...base}><path d="m9 6 6 6-6 6" /></svg>;
    case 'discord':
      return (
        <svg viewBox="0 0 24 24" style={s} fill="currentColor" aria-hidden>
          <path d="M19.6 5.4A18 18 0 0015.3 4l-.2.4a13 13 0 00-6.2 0L8.7 4a18 18 0 00-4.3 1.4C1.6 9.6.9 13.7 1.2 17.7a18 18 0 005.5 2.8c.4-.6.8-1.3 1.1-2-.7-.3-1.4-.6-2-1l.5-.4a13 13 0 0011.4 0l.5.4c-.6.4-1.3.7-2 1 .3.7.7 1.4 1.1 2a18 18 0 005.5-2.8c.5-4.6-.6-8.7-2.4-12.3zM8.5 15.3c-1.1 0-2-1-2-2.3 0-1.2.9-2.2 2-2.2s2 1 2 2.2c0 1.3-.9 2.3-2 2.3zm7 0c-1.1 0-2-1-2-2.3 0-1.2.9-2.2 2-2.2s2 1 2 2.2c0 1.3-.9 2.3-2 2.3z" />
        </svg>
      );
    case 'globe':
      return <svg {...base}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" /></svg>;
    case 'block':
      return <svg {...base}><path d="M12 3l9 5v8l-9 5-9-5V8z" /><path d="M3 8l9 5 9-5M12 13v10" /></svg>;
    case 'warn':
      return <svg {...base}><path d="M12 4l10 17H2L12 4z" /><path d="M12 10v5M12 18h.01" /></svg>;
    case 'clock':
      return <svg {...base}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    case 'cloud':
      return <svg {...base}><path d="M7 18h10a4 4 0 000-8 6 6 0 00-11.5-1A4 4 0 006 18z" /></svg>;
    case 'sun':
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      );
    case 'moon':
      return <svg {...base}><path d="M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z" /></svg>;
    case 'logout':
      return <svg {...base}><path d="M10 4H5v16h5" /><path d="M14 8l4 4-4 4M18 12H9" /></svg>;
    case 'chat':
      return <svg {...base}><path d="M4 5h16v11H9l-5 4V5z" /></svg>;
    case 'skull':
      return (
        <svg {...base}>
          <path d="M12 3a8 8 0 00-5 14.2V20h10v-2.8A8 8 0 0012 3z" />
          <circle cx="9" cy="11" r="1.4" /><circle cx="15" cy="11" r="1.4" /><path d="M11 16h2" />
        </svg>
      );
    case 'external':
      return <svg {...base}><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 14v6H4V6h6" /></svg>;
    case 'pause':
      return <svg {...base}><path d="M8 5v14M16 5v14" /></svg>;
    case 'activity':
      return <svg {...base}><path d="M3 13h4l3-8 4 14 3-6h4" /></svg>;
    case 'menu':
      return <svg {...base}><path d="M4 7h16M4 12h16M4 17h16" /></svg>;
    default:
      return null;
  }
};

export default Icon;
